import React from 'react'
import { ThemeProvider, createTheme, type Theme } from '@mui/material/styles'
import type { TypographyStyleOptions } from '@mui/material/styles/createTypography'
import { clampDrawerFontScale } from '../../store/userSettings'

interface PrepFontScalerProps {
  theme: Theme
  scale: number
  children: React.ReactNode
}

const VARIANTS: Array<'h6' | 'subtitle1' | 'subtitle2' | 'body1' | 'body2' | 'caption' | 'overline'> = [
  'h6',
  'subtitle1',
  'subtitle2',
  'body1',
  'body2',
  'caption',
  'overline',
]

const scaleMetric = (value: unknown, factor: number): unknown => {
  if (typeof value === 'number') {
    return Number((value * factor).toFixed(3))
  }

  if (typeof value === 'string') {
    const numeric = Number.parseFloat(value)
    if (!Number.isFinite(numeric)) {
      return value
    }

    const unitMatch = value.trim().match(/[a-z%]+$/i)
    const unit = unitMatch ? unitMatch[0] : ''
    const scaled = Number((numeric * factor).toFixed(3))
    return unit ? `${scaled}${unit}` : `${scaled}`
  }

  return value
}

const scaleTypographyStyle = (
  style: TypographyStyleOptions,
  factor: number,
): TypographyStyleOptions => {
  const next: TypographyStyleOptions = { ...style }

  if (Object.prototype.hasOwnProperty.call(next, 'fontSize')) {
    next.fontSize = scaleMetric(next.fontSize, factor)
  }

  if (Object.prototype.hasOwnProperty.call(next, 'lineHeight')) {
    next.lineHeight = scaleMetric(next.lineHeight, factor)
  }

  return next
}

const PrepFontScaler: React.FC<PrepFontScalerProps> = ({ theme, scale, children }) => {
  const normalizedScale = clampDrawerFontScale(scale)

  const scaledTheme = React.useMemo(() => {
    if (Math.abs(normalizedScale - 1) < 0.0001) {
      return theme
    }

    const overrides: Partial<Record<typeof VARIANTS[number], TypographyStyleOptions>> = {}
    const baseH6 = theme.typography.h6 as TypographyStyleOptions | undefined
    const pinnedH6LineHeight = (() => {
      const baseValue = baseH6?.lineHeight
      if (typeof baseValue === 'number') {
        return Math.min(baseValue, 1.15)
      }
      if (typeof baseValue === 'string') {
        const numeric = Number.parseFloat(baseValue)
        if (Number.isFinite(numeric)) {
          return Math.min(numeric, 1.15)
        }
      }
      return 1.15
    })()

    for (const variant of VARIANTS) {
      const base = theme.typography[variant] as TypographyStyleOptions | undefined
      if (!base) {
        continue
      }

      if (variant === 'h6') {
        const scaled = scaleTypographyStyle(base, normalizedScale)
        // Pin the h6 line-height (capped at a compact value) so that increasing
        // the drawer font scale doesn't create overly tall multi-line
        // headlines in the detail view.
        overrides[variant] = {
          ...scaled,
          lineHeight: typeof pinnedH6LineHeight === 'number' || typeof pinnedH6LineHeight === 'string'
            ? pinnedH6LineHeight
            : 1.15,
        }
        continue
      }

      overrides[variant] = scaleTypographyStyle(base, normalizedScale)
    }

    return createTheme(theme, {
      typography: overrides,
    })
  }, [theme, normalizedScale])

  if (scaledTheme === theme) {
    return <>{children}</>
  }

  return <ThemeProvider theme={scaledTheme}>{children}</ThemeProvider>
}

export default PrepFontScaler
